// Starter Kit Builder - Phase 4: Cross-browser Compatibility
// Full wizard functionality with browser compatibility and fallbacks

class StarterKitBuilder {
  constructor(section) {
    this.section = section;
    this.currentStep = 0; // Start at 0 (hero), then go to 1 (selection)
    this.selections = {
      set1: null,
      set2: null,
    };
    this.collection = {
      products: [],
      currentPage: 1,
      hasNextPage: true,
      loading: false,
    };
    this.modalState = {
      open: false,
      currentProduct: null,
      currentImageIndex: 0,
      scrollPosition: 0,
    };

    // Transition lock state
    this.isTransitioning = false;

    // Event binding state
    this.eventsBound = false;

    // Bound handler references (for cleanup in destroy())
    this._onDocumentClick = null;
    this._onDocumentKeydown = null;
    this._onDocumentTouchmove = null;
    this._onDocumentWheel = null;
    this._onDocumentTouchstart = null;
    this._onDocumentTouchendSwipe = null;
    this._onDocumentTouchendZoom = null;
    this._onWindowOrientationchange = null;

    // State for double-tap zoom prevention
    this._lastTouchEnd = 0;

    // Browser compatibility checks
    this.browserSupport = this.checkBrowserSupport();

    // Store instance globally for frontpage integration
    window.starterKitBuilderInstance = this;

    this.init();
  }

  checkBrowserSupport() {
    return {
      intersectionObserver: 'IntersectionObserver' in window,
      fetch: 'fetch' in window,
      promise: 'Promise' in window,
      classList: 'classList' in Element.prototype,
      addEventListener: 'addEventListener' in window,
      querySelector: 'querySelector' in document,
      touchEvents: 'ontouchstart' in window,
    };
  }

  init() {
    this.liveRegion = this.section.querySelector('.skf-live-region');
    this.bindEvents();
    // Note: Infinite scroll removed since products are pre-rendered in Liquid
    // this.initInfiniteScroll(); // Available for future AJAX implementation

    // Load products immediately so modal opening works from any step
    this.initializeProductsFromDOM();

    // Restore previous session state if available
    this.restoreSelectionState();

    // CSS handles initial hiding - JavaScript only manages showing
    this.updateUI();

    // Initialize V4A select button text observer
    this.initV4ASelectionObserver();

    // Initialize V4A modal enhancements
    this.initV4AModalObserver();

    // Initialize length filter pills
    this.initLengthFilter();

    // Initialize lazy loading for Fera reviews
    this.initLazyFeraReviews();

    // Mark as ready to show Step 0 without layout jump
    requestAnimationFrame(() => {
      this.section.classList.add('ready');
    });
  }

  initLazyFeraReviews() {
    if (!this.browserSupport.intersectionObserver) {
      // Fallback: load all Fera reviews immediately on older browsers
      document.querySelectorAll('[data-fera-lazy]').forEach((el) => {
        const feraId = el.getAttribute('data-fera-lazy');
        el.setAttribute('data-fera-container', feraId);
        el.removeAttribute('data-fera-lazy');
      });
      return;
    }

    const feraObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const feraId = el.getAttribute('data-fera-lazy');

            if (feraId) {
              // Activate Fera by changing data-fera-lazy to data-fera-container
              el.setAttribute('data-fera-container', feraId);
              el.removeAttribute('data-fera-lazy');

              // Stop observing this element
              feraObserver.unobserve(el);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Load when within 50px of viewport
      }
    );

    // Observe all Fera review containers
    document.querySelectorAll('[data-fera-lazy]').forEach((el) => {
      feraObserver.observe(el);
    });
  }

  initializeProductsFromDOM() {
    // Get all product cards from the product grid
    const allProductCards = this.section.querySelectorAll(
      '.product-card[data-product-id]'
    );

    allProductCards.forEach((card) => {
      const productId = parseInt(card.getAttribute('data-product-id'));
      const title =
        card.querySelector('h3')?.textContent?.trim() || 'Unknown Product';
      const priceElement = card.querySelector('.price');
      const price = priceElement ? priceElement.textContent.trim() : '';

      // Get availability from data attribute
      const availableAttr = card.getAttribute('data-available');
      const available = availableAttr === 'true';

      // Safely extract image data with fallbacks
      let featuredImage = null;
      const imageElement = card.querySelector('.product-image img');

      if (imageElement && imageElement.src) {
        featuredImage = {
          url: imageElement.src,
          alt: imageElement.alt || title,
        };
      }

      // Create product object with safe fallbacks
      const product = {
        id: productId,
        title: title,
        price: price,
        featured_image: featuredImage,
        images: featuredImage ? [featuredImage] : [],
        url: card.querySelector('a')?.href || '#',
        available: available,
      };

      // Validate required fields
      if (!productId || isNaN(productId)) {
        console.warn('⚠️ Skipping product with invalid ID:', card);
        return;
      }

      // Add to collection
      this.collection.products.push(product);
    });
  }

  bindEvents() {
    // Prevent multiple event bindings
    if (this.eventsBound) {
      return;
    }
    this.eventsBound = true;

    // Single click handler on document for all interactions
    this._onDocumentClick = (e) => {
      // Handle modal interactions first (modal might be outside section)
      if (this.modalState.open) {
        // Prevent any interaction with background elements when modal is open
        const clickedOnModal = e.target.closest('modal-dialog');

        if (e.target.closest('.select-button')) {
          e.preventDefault();
          e.stopPropagation();
          const button = e.target.closest('.select-button');
          const productId =
            button.dataset.productId || button.getAttribute('data-product-id');
          this.selectProduct(productId);
          return;
        }

        if (e.target.closest('.modal-close')) {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
          return;
        }

        if (e.target.closest('.skb-gallery-nav')) {
          e.preventDefault();
          e.stopPropagation();
          const navBtn = e.target.closest('.skb-gallery-nav');
          if (navBtn.classList.contains('skb-nav-prev')) {
            this.navigateGallery(-1);
          } else if (navBtn.classList.contains('skb-nav-next')) {
            this.navigateGallery(1);
          }
          return;
        }

        // Click on modal backdrop (not on modal content) - close modal
        if (e.target.matches('modal-dialog')) {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
          return;
        }

        // If modal is open and click is anywhere else, close the modal
        if (!clickedOnModal) {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
          return;
        }

        // Click is on modal content (not backdrop, buttons, or nav) - allow it but don't pass through
        e.stopPropagation();
        return;
      }

      // Handle section interactions (product cards, step navigation, etc.)
      if (!this.section || !this.section.contains(e.target)) {
        return;
      }

      // 1. Step navigation - only stepper buttons, not containers with data-step
      const stepElement = e.target.closest('.stepper [data-step]');
      if (stepElement) {
        e.preventDefault();

        // Don't do anything if the button is disabled
        if (
          stepElement.hasAttribute('disabled') ||
          stepElement.classList.contains('disabled')
        ) {
          return;
        }

        const targetStep = parseInt(stepElement.dataset.step);
        // Only bypass validation for backward navigation
        const bypassValidation = targetStep < this.currentStep;
        this.goToStep(targetStep, bypassValidation);
        return;
      }

      // Special handling for hero CTA button
      if (e.target.closest('.hero-cta')) {
        e.preventDefault();
        this.startBuilder();
        return;
      }

      // 2. Product card clicks - open modal (unless clicking the select button or sold out badge)
      const productCard = e.target.closest('.product-card[data-product-id]');
      if (productCard) {
        e.preventDefault();
        e.stopPropagation();

        const productId = productCard.dataset.productId;

        // Don't do anything if clicking on sold out badge
        if (e.target.closest('.sold-out-badge')) {
          return;
        }

        // Check if the click is on the select button
        const isSelectButton =
          e.target.hasAttribute('data-select-product') ||
          e.target.closest('[data-select-product]');

        if (isSelectButton) {
          // Clicking the select button - select the product
          const element = e.target.hasAttribute('data-select-product')
            ? e.target
            : e.target.closest('[data-select-product]');
          const selectProductId = element.dataset.selectProduct;
          if (selectProductId) {
            this.selectProduct(selectProductId);
          }
        } else {
          // Clicking anywhere else on the card - open modal (only if available)
          const available = productCard.dataset.available === 'true';
          if (productId && available) {
            this.openModal(productId);
          }
        }
        return;
      }

      // 3. Clickable set cards in summary (edit selections)
      const editableSet = e.target.closest('.clickable-set[data-edit-step]');
      if (editableSet) {
        e.preventDefault();
        const stepToEdit = parseInt(editableSet.dataset.editStep);
        this.goToStep(stepToEdit, true); // bypass validation for editing
        return;
      }
    };
    document.addEventListener('click', this._onDocumentClick);

    // Touch/swipe support for modal gallery
    this.initTouchGestures();

    // Keyboard navigation
    this._onDocumentKeydown = (e) => {
      if (this.modalState.open) {
        if (e.key === 'Escape') {
          this.closeModal();
        } else if (e.key === 'ArrowLeft') {
          this.navigateGallery(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigateGallery(1);
        }
      }
    };
    document.addEventListener('keydown', this._onDocumentKeydown);

    // Mobile viewport handling
    this.handleMobileViewport();


  }

  startBuilder() {
    // Clear any saved state when explicitly restarting
    try { sessionStorage.removeItem('starterKitSelections'); } catch (e) {}
    this.goToStep(1, true);
  }

  goToStep(stepNumber, bypassValidation = false, { silent = false } = {}) {
    if (stepNumber < 1 || stepNumber > 3) {
      console.warn('Invalid step number:', stepNumber);
      return;
    }

    const previousStep = this.currentStep;

    // Validation logic
    if (stepNumber === 3 && (!this.selections.set1 || !this.selections.set2)) {
      // Never allow going to step 3 without both selections complete
      return;
    }

    if (!bypassValidation && stepNumber > this.currentStep) {
      // Prevent going to step 2 without selecting set1 first
      if (stepNumber === 2 && !this.selections.set1) {
        return;
      }

      // Prevent going to step 3 without both selections
      if (
        stepNumber === 3 &&
        (!this.selections.set1 || !this.selections.set2)
      ) {
        return;
      }
    }

    // Allow all other navigation (backward navigation, manual navigation to valid steps)

    // Safety unlock: any successful navigation clears the transition lock
    this.isTransitioning = false;

    this.currentStep = stepNumber;
    this.saveSelectionState();

    // Reset length filter to "Alle negler" when entering step 1 or 2
    if (stepNumber === 1 || stepNumber === 2) {
      this.resetLengthFilter();
    }

    this.updateUI();

    // Announce step change to screen readers (skip if silent, e.g. auto-advance after product selection)
    if (!silent) {
      const translations = window.starterKitTranslations || {};
      const stepMsg = translations.stepChanged
        ? translations.stepChanged.replace('{{ step_number }}', stepNumber)
        : `Step ${stepNumber} of 3`;
      this.announceToScreenReader(stepMsg);
    }

    this.hideValidationError();

    // Smooth scroll to top when transitioning between steps
    if (
      (stepNumber === 1 && previousStep === 0) ||
      (stepNumber === 2 && previousStep === 1) ||
      (stepNumber === 3 && previousStep === 2)
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Scroll filter row into view when entering step 1 or 2 (e.g. editing from step 3)
    if ((stepNumber === 1 || stepNumber === 2) && previousStep === 3) {
      const filterRow = this.section.querySelector('.v4a-filter-row');
      if (filterRow) {
        filterRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  selectProduct(productId) {
    // Prevent multiple selections during transition
    if (this.isTransitioning) {
      return;
    }

    const product = this.getProductById(productId);
    if (!product) {
      console.error('❌ Product not found for ID:', productId);
      return;
    }

    // Check if product is available
    if (!product.available) {
      console.warn('⚠️ Cannot select unavailable product:', product.title);
      return;
    }

    if (this.currentStep === 1) {
      // If re-selecting the same product and Set 2 already exists, go straight to Step 3
      if (this.selections.set1?.id === product.id && this.selections.set2) {
        this.goToStep(3, true);
        if (this.modalState.open) this.closeModal();
        return;
      }

      this.selections.set1 = product;
      this.isTransitioning = true;
      this.saveSelectionState();

      // Announce selection to screen readers
      const t1 = window.starterKitTranslations || {};
      const msg1 = t1.productSelected
        ? t1.productSelected
            .replace('{{ product_title }}', product.title)
            .replace('{{ set_number }}', '1')
        : `${product.title} selected as set 1`;
      this.announceToScreenReader(msg1);

      // Update UI to show checkmark, then proceed after animation completes
      this.updateSelectedProductIndicators();

      // Wait for selection animation to finish before advancing
      const card1 = this.section.querySelector(
        `.product-card[data-product-id="${productId}"]`
      );
      const advanceToStep2 = () => {
        this.goToStep(2, false, { silent: true });
        this.isTransitioning = false;
      };
      if (card1) {
        card1.addEventListener('animationend', advanceToStep2, { once: true });
        // Safety timeout in case animationend never fires (e.g., reduced motion)
        setTimeout(() => {
          if (this.isTransitioning) advanceToStep2();
        }, 1000);
      } else {
        advanceToStep2();
      }
    } else if (this.currentStep === 2) {
      // If re-selecting the same product and Set 1 already exists, go straight to Step 3
      if (this.selections.set2?.id === product.id && this.selections.set1) {
        this.goToStep(3, true);
        if (this.modalState.open) this.closeModal();
        return;
      }

      this.selections.set2 = product;
      this.isTransitioning = true;
      this.saveSelectionState();

      // Announce selection to screen readers
      const t2 = window.starterKitTranslations || {};
      const msg2 = t2.productSelected
        ? t2.productSelected
            .replace('{{ product_title }}', product.title)
            .replace('{{ set_number }}', '2')
        : `${product.title} selected as set 2`;
      this.announceToScreenReader(msg2);

      // Update UI to show checkmark, then proceed after animation completes
      this.updateSelectedProductIndicators();

      // Wait for selection animation to finish before advancing
      const card2 = this.section.querySelector(
        `.product-card[data-product-id="${productId}"]`
      );
      const advanceToStep3 = () => {
        this.goToStep(3, false, { silent: true });
        this.isTransitioning = false;
      };
      if (card2) {
        card2.addEventListener('animationend', advanceToStep3, { once: true });
        // Safety timeout in case animationend never fires (e.g., reduced motion)
        setTimeout(() => {
          if (this.isTransitioning) advanceToStep3();
        }, 1000);
      } else {
        advanceToStep3();
      }
    } else {
    }

    // Only close modal if it was actually open (selection made from modal)
    if (this.modalState.open) {
      this.closeModal();
    }
  }

  async checkProductAvailability(productId) {
    try {
      const response = await fetch(`/products/${productId}.json`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.product.available;
    } catch (e) {
      // Network error — assume available and let server-side catch it
      return null;
    }
  }

  saveSelectionState() {
    try {
      const state = {
        currentStep: this.currentStep,
        set1Id: this.selections.set1?.id || null,
        set2Id: this.selections.set2?.id || null,
      };
      sessionStorage.setItem('starterKitSelections', JSON.stringify(state));
    } catch (e) {
      // sessionStorage unavailable (private browsing, quota) — silently skip
    }
  }

  restoreSelectionState() {
    try {
      const saved = sessionStorage.getItem('starterKitSelections');
      if (!saved) return false;

      const state = JSON.parse(saved);

      // Restore selections by looking up products by ID
      if (state.set1Id) {
        const product1 = this.getProductById(state.set1Id);
        if (product1) this.selections.set1 = product1;
      }
      if (state.set2Id) {
        const product2 = this.getProductById(state.set2Id);
        if (product2) this.selections.set2 = product2;
      }

      // Restore Step 3 on refresh (review page); otherwise start at Step 0
      if (state.currentStep === 3 && this.selections.set1 && this.selections.set2) {
        this.currentStep = 3;
      }

      return !!(this.selections.set1 || this.selections.set2);
    } catch (e) {
      return false;
    }
  }

  getProductCards() {
    return this.section.querySelectorAll('.product-card[data-product-id]');
  }

  getProductById(productId) {
    // First check already loaded products in collection
    let product = this.collection.products.find(
      (p) => p.id === parseInt(productId)
    );
    if (product) return product;

    // Check window.starterKitProducts for full product data
    if (window.starterKitProducts && window.starterKitProducts[productId]) {
      return window.starterKitProducts[productId];
    }

    return null;
  }

  updateUI() {
    // Update section data attribute for styling
    this.section.setAttribute('data-current-step', this.currentStep);

    // Update step indicators and enable/disable based on selections
    this.updateStepperButtons();

    // Show/hide step content using CSS classes
    this.section.querySelectorAll('[data-step-content]').forEach((el) => {
      const stepNum = parseInt(el.dataset.stepContent, 10);
      const shouldShow = stepNum === this.currentStep;
      el.classList.toggle('active', shouldShow);
      el.hidden = !shouldShow;
    });

    const v4aGrid = this.section.querySelector('.v4a-product-grid');
    if (v4aGrid) {
      const shouldShowGrid =
        this.currentStep === 1 || this.currentStep === 2;
      v4aGrid.hidden = !shouldShowGrid;
    }

    // Update selected product indicators
    this.updateSelectedProductIndicators();

    // Update summary with selections
    if (this.currentStep === 3) {
      this.updateSummary();
    }

    // Update progress indicator
    const stepper = this.section.querySelector('.stepper');
    if (stepper) {
      stepper.setAttribute('aria-label', `Step ${this.currentStep} of 3`);
    }
  }

  updateStepperButtons() {
    this.section.querySelectorAll('.stepper [data-step]').forEach((el) => {
      const stepNum = parseInt(el.dataset.step);

      // Mark active step
      el.classList.toggle('active', stepNum === this.currentStep);

      // Enable/disable based on selections
      let isEnabled = true;

      if (stepNum === 2) {
        // Step 2 requires set1 to be selected
        isEnabled = !!this.selections.set1;
      } else if (stepNum === 3) {
        // Step 3 requires both sets to be selected
        isEnabled = !!(this.selections.set1 && this.selections.set2);
      }

      // Always allow clicking on step 1 or previous steps
      if (stepNum === 1 || stepNum < this.currentStep) {
        isEnabled = true;
      }

      // Update disabled state
      if (isEnabled) {
        el.removeAttribute('disabled');
        el.classList.remove('disabled');
      } else {
        el.setAttribute('disabled', 'disabled');
        el.classList.add('disabled');
      }

      // Update aria-disabled for accessibility
      el.setAttribute('aria-disabled', !isEnabled);
    });
  }

  updateSelectedProductIndicators() {
    // Get all product cards
    const productCards = this.getProductCards();

    productCards.forEach((card) => {
      const productId = parseInt(card.dataset.productId);
      const isSelectedSet1 = this.selections.set1?.id === productId;
      const isSelectedSet2 = this.selections.set2?.id === productId;
      const isSelected = isSelectedSet1 || isSelectedSet2;

      // Check if this is a NEW selection (wasn't selected before)
      const wasSelected = card.classList.contains('selected');
      const isNewSelection = isSelected && !wasSelected;

      // Toggle general selected class
      card.classList.toggle('selected', isSelected);

      // Toggle specific set classes
      card.classList.toggle('selected-set1', isSelectedSet1);
      card.classList.toggle('selected-set2', isSelectedSet2);

      // If this is a new selection, manage animation classes
      if (isNewSelection) {
        // Add selected-animated after pulse completes (600ms) to prevent re-animation
        setTimeout(() => {
          card.classList.add('selected-animated');
        }, 650); // Slightly after pulse animation completes
        // Hide checkmark after 1.2 seconds
        setTimeout(() => {
          card.classList.add('checkmark-hidden');
        }, 1200);
      } else if (!isSelected) {
        // Remove classes if deselected
        card.classList.remove('selected-animated', 'checkmark-hidden');
      }
    });
  }

  updateSummary() {
    // Populate both summary cards with rich product data
    this._populateSummaryCard(1, this.selections.set1);
    this._populateSummaryCard(2, this.selections.set2);

    // Update form properties for cart
    const form = this.section.querySelector('product-form form');
    if (form) {
      const set1Input = form.querySelector('[data-set-1-property]');
      const set2Input = form.querySelector('[data-set-2-property]');
      const submitButton = form.querySelector('button[type="submit"]');

      if (set1Input) set1Input.value = this.selections.set1?.title || '';
      if (set2Input) set2Input.value = this.selections.set2?.title || '';

      // Check if selected products are available
      let set1Available = this.selections.set1?.available !== false;
      let set2Available = this.selections.set2?.available !== false;

      // Re-check availability in background (non-blocking, skip if already known unavailable)
      if (this.selections.set1 && this.selections.set2 &&
          this.selections.set1.available !== false && this.selections.set2.available !== false) {
        Promise.all([
          this.checkProductAvailability(this.selections.set1.id),
          this.checkProductAvailability(this.selections.set2.id),
        ]).then(([avail1, avail2]) => {
          if (avail1 === false || avail2 === false) {
            // Update stored availability
            if (avail1 === false) this.selections.set1.available = false;
            if (avail2 === false) this.selections.set2.available = false;
            // Re-render summary to show unavailable state
            this.updateSummary();
          }
        });
      }

      const bothSetsAvailable = set1Available && set2Available;

      // Check if both sets are selected
      const canSubmit =
        this.selections.set1 && this.selections.set2 && bothSetsAvailable;

      if (submitButton) {
        const buttonTextSpan = submitButton.querySelector('span');

        if (!this.selections.set1 || !this.selections.set2) {
          // Not both sets selected
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('disabled', 'disabled');
          submitButton.setAttribute(
            'aria-label',
            'Please select both sets to continue'
          );
        } else if (!bothSetsAvailable) {
          // One or both sets are sold out
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('disabled', 'disabled');

          // Update button text to show sold out
          if (buttonTextSpan) {
            // Get sold out text from data attribute (set by Liquid based on locale)
            const soldOutText =
              submitButton.getAttribute('data-sold-out-text') || 'Sold out';
            buttonTextSpan.textContent = soldOutText;
          }

          submitButton.setAttribute(
            'aria-label',
            'One or more selected products are sold out'
          );
        } else {
          // All good - can add to cart
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('disabled');

          // Restore original button text
          if (buttonTextSpan) {
            const addToCartText =
              submitButton.getAttribute('data-add-to-cart-text') ||
              'Add to Cart';
            buttonTextSpan.textContent = addToCartText;
          }

          submitButton.setAttribute('aria-label', 'Add to cart');
        }
      }
    }
  }

  _populateSummaryCard(setNumber, selection) {
    const card = this.section.querySelector(`[data-selected-set="${setNumber}"]`);
    if (!card || !selection) return;

    // Get rich data from window.starterKitProducts (has type, length_badge)
    const richData = window.starterKitProducts?.[selection.id];

    // Title
    const titleEl = card.querySelector('.summary-card__title');
    if (titleEl) titleEl.textContent = selection.title;

    // Image
    const imageContainer = card.querySelector('.summary-card__image');
    if (imageContainer) {
      if (selection.images?.[0]?.url) {
        imageContainer.innerHTML = `<img src="${selection.images[0].url}" alt="${selection.title}" loading="lazy">`;
      }
    }

    // Product type (from richData)
    const typeEl = card.querySelector('.summary-card__type');
    if (typeEl) {
      typeEl.textContent = richData?.type || '';
      typeEl.style.display = richData?.type ? '' : 'none';
    }

    // Length badge
    const badgeEl = card.querySelector('.summary-card__length-badge');
    const dotEl = card.querySelector('.summary-card__meta-dot');
    if (badgeEl) {
      const badge = richData?.length_badge;
      badgeEl.textContent = badge || '';
      badgeEl.hidden = !badge;
      if (dotEl) dotEl.hidden = !badge;
    }

    // Fera reviews — show/hide pre-rendered containers (same pattern as modal)
    const reviewsEl = card.querySelector('.summary-card__reviews');
    if (reviewsEl) {
      reviewsEl.querySelectorAll('.fera-review-container').forEach((el) => {
        el.style.display = 'none';
      });
      const match = reviewsEl.querySelector(`[data-product-id="${selection.id}"]`);
      if (match) match.style.display = '';
    }
  }

  showValidationError(message) {
    let errorDiv = this.section.querySelector('.validation-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'validation-error';
      errorDiv.setAttribute('role', 'alert');
      errorDiv.setAttribute('aria-live', 'assertive');
      errorDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc3545;
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        z-index: 1000;
        max-width: 300px;
      `;
      this.section.appendChild(errorDiv);
    }

    errorDiv.textContent = message;
    errorDiv.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideValidationError();
    }, 5000);
  }

  hideValidationError() {
    const errorDiv = this.section.querySelector('.validation-error');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
  }

  announceToScreenReader(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }

  openModal(productId) {
    // Get the single reusable modal
    const modal = document.getElementById('starter-kit-modal');

    if (!modal) {
      console.error('❌ Modal element not found');
      return;
    }

    // Get product data from window object
    const product =
      window.starterKitProducts && window.starterKitProducts[productId];

    if (!product) {
      console.error('❌ Product data not found for ID:', productId);
      return;
    }

    // Close existing modal if open
    if (this.modalState.open) {
      this.closeModal();
    }

    // Populate modal with product data
    this.populateModal(modal, product);

    // Update modal state
    this.modalState.open = true;
    this.modalState.currentProduct = product;
    this.modalState.currentImageIndex = 0;

    // Lock body scroll
    this.modalState.scrollPosition = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.modalState.scrollPosition}px`;
    document.body.style.width = '100%';

    // Open the modal
    if (typeof modal.show === 'function') {
      modal.show();
    } else {
      modal.setAttribute('open', '');
    }

    this.updateGalleryDisplay();
    this.updateGalleryNavigation();

    // Trap focus inside modal for keyboard accessibility
    trapFocus(modal, modal.querySelector('.modal-close') || modal);

    // Announce modal open to screen readers
    const openT = window.starterKitTranslations || {};
    const openMsg = openT.modalOpened
      ? openT.modalOpened.replace('{{ product_title }}', product.title)
      : `Product details for ${product.title}`;
    this.announceToScreenReader(openMsg);
  }

  populateModal(modal, product) {
    // Update title
    const titleElement = modal.querySelector('#modal-title');
    if (titleElement) {
      titleElement.textContent = product.title;
    }

    // Update gallery
    const gallery = modal.querySelector('[data-modal-gallery]');
    if (gallery) {
      gallery.innerHTML = '';

      // Create all slides with media elements upfront (parallel preloading)
      // First two slides get high fetch priority, remaining get low
      product.media.forEach((media, index) => {
        const slide = document.createElement('div');
        slide.className = 'skb-gallery-item' + (index === 0 ? ' active' : '');
        slide.setAttribute('data-slide-index', index + 1);
        slide.setAttribute('data-media-type', media.media_type);

        // Store media data on slide for reference
        slide._mediaData = media;
        slide._productTitle = product.title;

        // Create media elements for ALL slides immediately
        if (media.media_type === 'video') {
          if (media.preview_image) {
            new Image().src = media.preview_image;
          }
          this.createVideoElement(slide, media);
        } else if (media.media_type === 'external_video') {
          this.createExternalVideoElement(slide, media);
        } else if (media.media_type === 'image') {
          const isHighPriority = index <= 1;
          this.createImageElement(slide, media, product.title, isHighPriority);
          // Set low priority for background slides (index 2+)
          if (index > 1) {
            const img = slide.querySelector('img');
            if (img) img.fetchPriority = 'low';
          }
        }

        gallery.appendChild(slide);
      });

      // Add navigation buttons if multiple images
      if (product.media.length > 1) {
        const prevBtn = document.createElement('button');
        prevBtn.className = 'skb-overlay-btn skb-gallery-nav skb-nav-prev';
        prevBtn.setAttribute('aria-label', 'Previous image');
        prevBtn.innerHTML =
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>';
        gallery.appendChild(prevBtn);

        const nextBtn = document.createElement('button');
        nextBtn.className = 'skb-overlay-btn skb-gallery-nav skb-nav-next';
        nextBtn.setAttribute('aria-label', 'Next image');
        nextBtn.innerHTML =
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>';
        gallery.appendChild(nextBtn);
      }

    }

    // Update select button / sold out badge
    const selectButton = modal.querySelector('[data-modal-select-button]');
    const soldOutBadge = modal.querySelector('[data-modal-sold-out]');

    if (product.available) {
      if (selectButton) {
        selectButton.style.display = 'block';
        selectButton.setAttribute('data-product-id', product.id);
      }
      if (soldOutBadge) {
        soldOutBadge.style.display = 'none';
      }
    } else {
      if (selectButton) {
        selectButton.style.display = 'none';
      }
      if (soldOutBadge) {
        soldOutBadge.style.display = 'block';
      }
    }

    // Show the correct pre-rendered Fera review container for this product
    const reviewContainer = modal.querySelector(
      '[data-modal-review-container]'
    );
    if (reviewContainer) {
      // Hide all review containers
      reviewContainer
        .querySelectorAll('.fera-review-container')
        .forEach((el) => {
          el.style.display = 'none';
        });

      // Show the one for this product
      const productReviewContainer = reviewContainer.querySelector(
        `[data-product-id="${product.id}"]`
      );
      if (productReviewContainer) {
        productReviewContainer.style.display = 'block';
      }
    }
  }

  closeModal() {
    const openModal = document.querySelector('modal-dialog[open]');
    if (openModal) {
      // Call the modal's hide() method to properly clean up (removes overflow-hidden from body)
      if (typeof openModal.hide === 'function') {
        openModal.hide();
      } else {
        // Fallback: manually remove attribute and overflow-hidden class
        openModal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }

      // Release focus trap and return focus to the page
      removeTrapFocus();

      // Clear modal content and stop all videos
      const gallery = openModal.querySelector('[data-modal-gallery]');
      if (gallery) {
        // Pause all videos before clearing
        const videos = gallery.querySelectorAll('video');
        videos.forEach((video) => {
          video.pause();
        });

        gallery.innerHTML = '';
      }

      const titleElement = openModal.querySelector('#modal-title');
      if (titleElement) {
        titleElement.textContent = '';
      }

      // Hide buttons
      const selectButton = openModal.querySelector(
        '[data-modal-select-button]'
      );
      const soldOutBadge = openModal.querySelector('[data-modal-sold-out]');
      if (selectButton) selectButton.style.display = 'none';
      if (soldOutBadge) soldOutBadge.style.display = 'none';
    }

    // Unlock body scroll and restore scroll position
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position
    if (this.modalState.scrollPosition !== undefined) {
      window.scrollTo(0, this.modalState.scrollPosition);
    }

    // Always reset modal state, even if no modal was found
    this.modalState.open = false;
    this.modalState.currentProduct = null;
    this.modalState.currentImageIndex = 0;

    // Announce modal close to screen readers
    const closeT = window.starterKitTranslations || {};
    const closeMsg = closeT.modalClosed || 'Product details closed';
    this.announceToScreenReader(closeMsg);
  }

  navigateGallery(direction) {
    // Count actual slides in the modal instead of relying on stored product data
    const modal = document.querySelector('modal-dialog[open]');
    if (!modal) return;

    const slides = modal.querySelectorAll('.skb-gallery-item');
    const totalImages = slides.length;

    if (totalImages <= 1) return;

    this.modalState.currentImageIndex =
      (this.modalState.currentImageIndex + direction + totalImages) %
      totalImages;

    this.updateGalleryDisplay();
    this.updateGalleryNavigation();
  }

  createVideoElement(slide, media) {
    const videoWrapper = document.createElement('div');
    videoWrapper.className = 'skb-video-wrapper';

    const video = document.createElement('video');
    video.className = 'skb-video';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata'; // Only load metadata, not full video
    if (media.preview_image) {
      video.poster = media.preview_image;
    }

    media.sources.forEach((source) => {
      const sourceEl = document.createElement('source');
      sourceEl.src = source.url;
      sourceEl.type = source.mime_type;
      video.appendChild(sourceEl);
    });

    videoWrapper.appendChild(video);

    if (media.preview_image) {
      const poster = document.createElement('img');
      poster.className = 'skb-video-poster';
      poster.src = media.preview_image;
      poster.alt = '';
      poster.loading = 'eager';
      videoWrapper.appendChild(poster);
    }

    videoWrapper.appendChild(this.createVideoPauseIcon());
    slide.appendChild(videoWrapper);
  }

  createExternalVideoElement(slide, media) {
    const videoWrapper = document.createElement('div');
    videoWrapper.className = 'skb-video-wrapper';

    const iframe = document.createElement('iframe');
    let videoUrl = '';
    if (media.host === 'youtube') {
      videoUrl = `https://www.youtube.com/embed/${media.external_id}?autoplay=1&loop=1&mute=1&playlist=${media.external_id}`;
    } else if (media.host === 'vimeo') {
      videoUrl = `https://player.vimeo.com/video/${media.external_id}?autoplay=1&loop=1&muted=1`;
    }
    iframe.src = videoUrl;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');
    iframe.style.width = '100%';
    iframe.style.height = '100%';

    videoWrapper.appendChild(iframe);
    videoWrapper.appendChild(this.createVideoPauseIcon());
    slide.appendChild(videoWrapper);
  }

  createVideoPauseIcon() {
    const btn = document.createElement('span');
    btn.className = 'skb-overlay-btn skb-video-pause-icon';
    btn.setAttribute('aria-hidden', 'true');
    btn.innerHTML = '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.2 0.75C0.813401 0.75 0.5 0.985051 0.5 1.275V10.725C0.5 11.0149 0.813401 11.0149 1.2 11.25C1.5866 11.25 1.9 11.0149 1.9 10.725V1.275C1.9 0.985051 1.5866 0.75 1.2 0.75Z" fill="currentColor"/><path d="M6.8 0.75C6.4134 0.75 6.1 0.985051 6.1 1.275V10.725C6.1 11.0149 6.4134 11.25 6.8 11.25C7.1866 11.25 7.5 11.0149 7.5 10.725V1.275C7.5 0.985051 7.1866 0.75 6.8 0.75Z" fill="currentColor"/></svg>';
    return btn;
  }

  createImageElement(slide, media, productTitle, isEager = false) {
    const img = document.createElement('img');
    img.className = 'skb-gallery-image';

    // Build srcset for responsive images
    const srcsetParts = [];
    if (media.image_url) {
      const url600 = media.image_url.includes('?')
        ? `${media.image_url}&width=600`
        : `${media.image_url}?width=600`;
      srcsetParts.push(`${url600} 600w`);
    }
    if (media.image_url_800) srcsetParts.push(`${media.image_url_800} 800w`);
    if (media.image_url_1200) srcsetParts.push(`${media.image_url_1200} 1200w`);
    if (media.image_url_1600) srcsetParts.push(`${media.image_url_1600} 1600w`);

    if (srcsetParts.length > 0) {
      img.srcset = srcsetParts.join(', ');
      img.sizes = '(max-width: 750px) 50vw, 600px';
    }

    // Use highest quality as fallback
    img.src =
      media.image_url_1600 ||
      media.image_url_1200 ||
      media.image_url_800 ||
      media.image_url;
    img.alt = media.alt || productTitle;

    if (isEager) {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    } else {
      img.loading = 'eager';
      img.fetchPriority = 'auto';
    }
    img.decoding = 'async';

    // Set intrinsic dimensions to prevent Chrome from recalculating srcset selection
    if (media.width && media.height) {
      img.width = media.width;
      img.height = media.height;
    }

    img.onerror = () => {
      slide.classList.remove('loading');
      slide.classList.add('error');
      const errorDiv = document.createElement('div');
      errorDiv.className = 'skb-gallery-error';
      errorDiv.innerHTML = '<p>Bildet kunne ikke lastes</p><button type="button">Prøv igjen</button>';
      errorDiv.querySelector('button').addEventListener('click', () => {
        slide.classList.remove('error');
        slide.classList.add('loading');
        errorDiv.remove();
        img.src = img.src; // Retry by re-assigning src
      }, { once: true });
      slide.appendChild(errorDiv);
    };

    img.onload = () => {
      slide.classList.remove('loading');
    };

    slide.appendChild(img);
  }

  updateGalleryDisplay() {
    const modal = document.querySelector('modal-dialog[open]');
    if (!modal) return;

    const slides = modal.querySelectorAll('.skb-gallery-item');
    let outgoingSlide = null;
    let incomingSlide = null;

    // Invalidate any pending transition from rapid navigation
    this._galleryTransitionId = (this._galleryTransitionId || 0) + 1;
    const thisTransitionId = this._galleryTransitionId;

    // Phase 1: Identify outgoing/incoming
    // Key: do NOT add .active to incoming yet — wait for decode
    slides.forEach((slide, index) => {
      const isTarget = index === this.modalState.currentImageIndex;

      if (isTarget) {
        // This is the target slide
        slide.classList.remove('outgoing');
        incomingSlide = slide;
        // If already .active (initial load), leave it — no transition needed
      } else if (slide.classList.contains('active') || slide.classList.contains('outgoing')) {
        // This slide is currently visible — it becomes the outgoing fallback
        // If we already found an outgoing, hide the previous one
        if (outgoingSlide && outgoingSlide !== slide) {
          outgoingSlide.classList.remove('active', 'outgoing');
        }
        outgoingSlide = slide;
        slide.classList.remove('active');
        slide.classList.add('outgoing');
      } else {
        // Not visible and not target — ensure hidden
        slide.classList.remove('active', 'outgoing');
      }
    });

    // Phase 2: Ensure incoming slide has media content (defensive — should already exist from populateModal)
    if (incomingSlide && incomingSlide._mediaData) {
      const mediaType = incomingSlide.getAttribute('data-media-type');

      if (mediaType === 'image' && !incomingSlide.querySelector('img')) {
        this.createImageElement(
          incomingSlide,
          incomingSlide._mediaData,
          incomingSlide._productTitle,
          true
        );
      }

      if (mediaType === 'video' && !incomingSlide.querySelector('video')) {
        this.createVideoElement(incomingSlide, incomingSlide._mediaData);
      }

      if (mediaType === 'external_video' && !incomingSlide.querySelector('iframe')) {
        this.createExternalVideoElement(incomingSlide, incomingSlide._mediaData);
      }
    }

    // Phase 3: Wait for content to be ready, then activate incoming + remove outgoing atomically
    const activateSlide = () => {
      // Only activate if this is still the current transition (handles rapid navigation)
      if (this._galleryTransitionId !== thisTransitionId) return;

      if (incomingSlide && !incomingSlide.classList.contains('active')) {
        incomingSlide.classList.add('active');
      }
      if (outgoingSlide) {
        outgoingSlide.classList.remove('outgoing');
      }
    };

    if (outgoingSlide && incomingSlide) {
      const mediaType = incomingSlide.getAttribute('data-media-type');

      if (mediaType === 'image') {
        const img = incomingSlide.querySelector('img');
        if (img && img.complete) {
          // Image already loaded — decode then activate
          if (typeof img.decode === 'function') {
            img.decode().then(activateSlide).catch(activateSlide);
          } else {
            activateSlide();
          }
        } else if (img) {
          // Image still loading — wait for load, then decode, then activate
          img.addEventListener('load', () => {
            if (typeof img.decode === 'function') {
              img.decode().then(activateSlide).catch(activateSlide);
            } else {
              activateSlide();
            }
          }, { once: true });
          img.addEventListener('error', activateSlide, { once: true });
        } else {
          activateSlide();
        }
      } else if (mediaType === 'video') {
        // Wait for poster overlay image to be ready before showing video slide
        const posterImg = incomingSlide.querySelector('.skb-video-poster');
        if (posterImg) {
          // Reset poster visibility for this navigation
          posterImg.classList.remove('hidden');
          if (posterImg.complete) {
            activateSlide();
          } else {
            posterImg.onload = activateSlide;
            posterImg.onerror = activateSlide;
          }
        } else {
          // No poster overlay — use double rAF
          requestAnimationFrame(() => {
            requestAnimationFrame(activateSlide);
          });
        }
      } else {
        // External video or unknown — use double rAF
        requestAnimationFrame(() => {
          requestAnimationFrame(activateSlide);
        });
      }

      // Safety timeout — don't leave the UI stuck
      setTimeout(() => {
        if (this._galleryTransitionId === thisTransitionId) {
          activateSlide();
        }
      }, 2000);
    } else if (!outgoingSlide && incomingSlide) {
      // No outgoing (initial load) — ensure slide is active
      if (!incomingSlide.classList.contains('active')) {
        incomingSlide.classList.add('active');
      }
    }

    // Phase 4: Handle video playback
    slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!video) return;

      if (index === this.modalState.currentImageIndex) {
        const posterImg = slide.querySelector('.skb-video-poster');

        // Fade out poster once video has renderable frames
        const onPlaying = () => {
          video.removeEventListener('playing', onPlaying);
          if (posterImg) posterImg.classList.add('hidden');
        };
        video.addEventListener('playing', onPlaying);

        video.play().catch(() => {
          // Autoplay blocked — keep poster visible
          video.removeEventListener('playing', onPlaying);
        });
      } else {
        video.pause();
        // Reset poster when navigating away from a video slide
        const posterImg = slide.querySelector('.skb-video-poster');
        if (posterImg) posterImg.classList.remove('hidden');
      }
    });
  }

  updateGalleryNavigation() {
    const modal = document.querySelector('modal-dialog[open]');
    if (!modal) return;

    const prevBtn = modal.querySelector('.skb-nav-prev');
    const nextBtn = modal.querySelector('.skb-nav-next');
    const slides = modal.querySelectorAll('.skb-gallery-item');
    const totalImages = slides.length;

    // Hide navigation buttons if there's only 1 image or no images
    if (totalImages <= 1) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      return;
    }

    // Show navigation buttons for multiple images (wraparound enabled)
    if (prevBtn) {
      prevBtn.style.display = 'flex';
      prevBtn.disabled = false;
    }

    if (nextBtn) {
      nextBtn.style.display = 'flex';
      nextBtn.disabled = false;
    }
  }

  initTouchGestures() {
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchEndX = null;
    this.touchEndY = null;

    // Prevent background scrolling when modal is open (touch)
    this._onDocumentTouchmove = (e) => {
      if (this.modalState.open) {
        // Allow scrolling within modal content, but prevent background scrolling
        const target = e.target;
        const modalContent = target.closest('.modal-content');
        const modalInfo = target.closest('.modal-info');

        // If touch is not on modal content or scrollable modal info, prevent default
        if (!modalContent && !modalInfo) {
          e.preventDefault();
        }
      }
    };
    document.addEventListener('touchmove', this._onDocumentTouchmove, {
      passive: false,
    });

    // Prevent background scrolling when modal is open (mouse wheel)
    this._onDocumentWheel = (e) => {
      if (this.modalState.open) {
        // Allow scrolling within modal content, but prevent background scrolling
        const target = e.target;
        const modalContent = target.closest('.modal-content');
        const modalInfo = target.closest('.modal-info');

        // If wheel event is not on modal content or scrollable modal info, prevent default
        if (!modalContent && !modalInfo) {
          e.preventDefault();
        }
      }
    };
    document.addEventListener('wheel', this._onDocumentWheel, {
      passive: false,
    });

    // Touch events for modal gallery swipe (listen on document since modal moves to body)
    this._onDocumentTouchstart = (e) => {
      if (this.modalState.open) {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
      }
    };
    document.addEventListener('touchstart', this._onDocumentTouchstart, {
      passive: true,
    });

    this._onDocumentTouchendSwipe = (e) => {
      if (this.modalState.open && this.touchStartX !== null) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        this.handleSwipeGesture();
      }
    };
    document.addEventListener('touchend', this._onDocumentTouchendSwipe, {
      passive: true,
    });
  }

  handleSwipeGesture() {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const minSwipeDistance = Math.max(30, Math.ceil(window.innerWidth * 0.08));

    // Only handle horizontal swipes (ignore mostly vertical ones)
    if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > minSwipeDistance
    ) {
      if (deltaX > 0) {
        // Swipe right - previous image
        this.navigateGallery(-1);
      } else {
        // Swipe left - next image
        this.navigateGallery(1);
      }
    }

    // Reset touch coordinates
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchEndX = null;
    this.touchEndY = null;
  }

  handleMobileViewport() {
    // Prevent zoom on double tap for iOS
    this._onDocumentTouchendZoom = (e) => {
      const now = Date.now();
      if (now - this._lastTouchEnd <= 300) {
        e.preventDefault();
      }
      this._lastTouchEnd = now;
    };
    document.addEventListener('touchend', this._onDocumentTouchendZoom, {
      passive: false,
    });

    // Handle orientation changes
    this._onWindowOrientationchange = () => {
      // Small delay to allow viewport to update
      setTimeout(() => {
        if (this.modalState.open) {
          this.updateGalleryNavigation();
        }
      }, 100);
    };
    window.addEventListener('orientationchange', this._onWindowOrientationchange);
  }

  // V4A: Update select button text (Velg/Valgt) based on selection state
  updateV4ASelectButtons() {
    const v4aGrid = this.section.querySelector('.v4a-product-grid');
    if (!v4aGrid) return;

    const v4aCards = v4aGrid.querySelectorAll('.v4a-card[data-product-id]');
    v4aCards.forEach((card) => {
      const isSelected = card.classList.contains('selected');
      const textEl = card.querySelector('.v4a-card__select-text');
      if (textEl) {
        textEl.textContent = isSelected ? 'Valgt' : 'Velg';
      }
    });
  }

  // V4A: Length filter pills — show/hide cards by data-length
  initLengthFilter() {
    const filterRows = this.section.querySelectorAll('.v4a-filter-row');
    if (!filterRows.length) return;

    filterRows.forEach((row) => {
      row.addEventListener('click', (e) => {
        const pill = e.target.closest('.v4a-filter-pill');
        if (!pill) return;

        // Update active pill and scroll it fully into view
        row.querySelectorAll('.v4a-filter-pill').forEach((p) => p.classList.remove('active'));
        pill.classList.add('active');

        const rowRect = row.getBoundingClientRect();
        const pillRect = pill.getBoundingClientRect();
        if (pillRect.left < rowRect.left) {
          row.scrollBy({ left: pillRect.left - rowRect.left - 12, behavior: 'smooth' });
        } else if (pillRect.right > rowRect.right) {
          row.scrollBy({ left: pillRect.right - rowRect.right + 12, behavior: 'smooth' });
        }

        const filter = pill.dataset.filter;
        const grid = row.closest('.v4a-grid');
        if (!grid) return;

        const cards = grid.querySelectorAll('.v4a-card');
        cards.forEach((card) => {
          if (!filter || card.dataset.length === filter) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      });
    });
  }

  // V4A: Reset length filter to "Alle negler" and show all cards
  resetLengthFilter() {
    this.section.querySelectorAll('.v4a-filter-row').forEach((row) => {
      row.querySelectorAll('.v4a-filter-pill').forEach((p) => p.classList.remove('active'));
      const allPill = row.querySelector('.v4a-filter-pill[data-filter=""]');
      if (allPill) allPill.classList.add('active');
      row.scrollTo({ left: 0, behavior: 'instant' });
    });
    this.section.querySelectorAll('.v4a-card').forEach((card) => {
      card.style.display = '';
    });
  }

  // V4A: Watch for .selected class changes on V4A cards to update button text
  initV4ASelectionObserver() {
    const v4aGrid = this.section.querySelector('.v4a-product-grid');
    if (!v4aGrid) return;

    this._v4aSelectionObserver = new MutationObserver((mutations) => {
      let needsUpdate = false;
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target.classList.contains('v4a-card')
        ) {
          needsUpdate = true;
          break;
        }
      }
      if (needsUpdate) {
        this.updateV4ASelectButtons();
      }
    });

    this._v4aSelectionObserver.observe(v4aGrid, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  // V4A: Watch for modal open/close to add type badge and size badge
  initV4AModalObserver() {
    const modal = document.getElementById('starter-kit-modal');
    if (!modal) return;

    this._v4aModalObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (modal.hasAttribute('open')) {
            this.enhanceV4AModal(modal);
          } else {
            this.cleanupV4AModal(modal);
          }
        }
      }
    });

    this._v4aModalObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  enhanceV4AModal(modal) {
    if (!this.modalState.currentProduct) return;

    const product = this.modalState.currentProduct;
    const productData = window.starterKitProducts && window.starterKitProducts[product.id];
    if (!productData) return;

    const modalInfo = modal.querySelector('.modal-info');
    if (!modalInfo) return;

    // Remove any previous enhancements
    this.cleanupV4AModal(modal);

    const titleEl = modalInfo.querySelector('#modal-title');
    const badgeRatingRow = modalInfo.querySelector('[data-modal-badge-rating-row]');

    // Add product type AFTER title
    if (productData.type && titleEl) {
      const typeEl = document.createElement('div');
      typeEl.className = 'v4a-modal-type';
      typeEl.textContent = productData.type;
      titleEl.after(typeEl);
    }

    // Add size badge INSIDE the badge-rating row (before stars)
    if (productData.length_badge && badgeRatingRow) {
      const badgeEl = document.createElement('span');
      badgeEl.className = 'v4a-modal-badge';
      badgeEl.textContent = productData.length_badge;
      badgeRatingRow.insertBefore(badgeEl, badgeRatingRow.firstChild);
    }
  }

  cleanupV4AModal(modal) {
    const existing = modal.querySelectorAll('.v4a-modal-type, .v4a-modal-badge');
    existing.forEach((el) => el.remove());
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this._v4aSelectionObserver) {
      this._v4aSelectionObserver.disconnect();
    }
    if (this._v4aModalObserver) {
      this._v4aModalObserver.disconnect();
    }

    // Remove all document/window event listeners
    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }
    if (this._onDocumentKeydown) {
      document.removeEventListener('keydown', this._onDocumentKeydown);
    }
    if (this._onDocumentTouchmove) {
      document.removeEventListener('touchmove', this._onDocumentTouchmove);
    }
    if (this._onDocumentWheel) {
      document.removeEventListener('wheel', this._onDocumentWheel);
    }
    if (this._onDocumentTouchstart) {
      document.removeEventListener('touchstart', this._onDocumentTouchstart);
    }
    if (this._onDocumentTouchendSwipe) {
      document.removeEventListener('touchend', this._onDocumentTouchendSwipe);
    }
    if (this._onDocumentTouchendZoom) {
      document.removeEventListener('touchend', this._onDocumentTouchendZoom);
    }
    if (this._onWindowOrientationchange) {
      window.removeEventListener(
        'orientationchange',
        this._onWindowOrientationchange
      );
    }

    // Clear handler references
    this._onDocumentClick = null;
    this._onDocumentKeydown = null;
    this._onDocumentTouchmove = null;
    this._onDocumentWheel = null;
    this._onDocumentTouchstart = null;
    this._onDocumentTouchendSwipe = null;
    this._onDocumentTouchendZoom = null;
    this._onWindowOrientationchange = null;

    // Reset binding state so re-initialization works
    this.eventsBound = false;

    // Clean up DOM elements
    const errorDivs = this.section.querySelectorAll(
      '.validation-error, .error-message, .success-message, .cart-error-message'
    );
    errorDivs.forEach((div) => div.remove());
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-starter-kit-builder]');

  sections.forEach((section) => {
    try {
      // Destroy previous instance if re-initializing
      if (section._starterKitBuilder) {
        section._starterKitBuilder.destroy();
      }
      const builder = new StarterKitBuilder(section);
      section._starterKitBuilder = builder; // Store reference
    } catch (error) {
      console.error('❌ Starter Kit Builder initialization failed:', error);
      console.error('Error stack:', error.stack);
    }
  });
});
